import {Nav} from "react-bootstrap"
import {useTheme} from "../../context/ThemeProvider"
import axios from "axios"
import {Link, useLocation} from "react-router-dom"
import {useTranslation} from "react-i18next"
import {useAuth} from "../../context/AuthProvider"
import {getNavigationItems, NavigationItem} from "./navigation.config"
import styles from "./MainNavigation.module.scss"

type Props = {
    collapsed: boolean
    onNavigate?: () => void
}

export default function MainSidebar({collapsed, onNavigate}: Props) {
    const auth = useAuth()
    const theme = useTheme()
    const loggedIn = auth.isAuthenticated()
    const {t, i18n} = useTranslation([
        "common",
        "tenant",
        "navigation", "booking", "info", "site", "settings",
    ])
    const location = useLocation()

    const items = getNavigationItems(auth, t).filter(
        (item: NavigationItem) => item.visible
    )

    const workspaceItems = items.filter(
        (item: NavigationItem) => item.section === "workspace"
    )

    const administrationItems = items.filter(
        (item: NavigationItem) => item.section === "administration"
    )

    const active = (path: string): boolean => {
        return location.pathname === path ||
            location.pathname.startsWith(path + "/")
    }

    const changeLanguage = async (
        language: "de" | "en"
    ): Promise<void> => {
        await i18n.changeLanguage(language)

        if (loggedIn) {
            await axios.get(
                `/api/user/profile/lang/${language}`,
                auth.axiosAuthHeader()
            )
        }
    }

    const renderItem = (item: NavigationItem) => (
        <Nav.Link
            key={item.id}
            as={Link}
            to={item.to}
            active={active(item.to)}
            className={styles.sidebarLink}
            onClick={() => onNavigate?.()}
            title={collapsed ? item.label : undefined}
        >
            <i className={`bi ${item.icon} ${collapsed ? "" : "me-2"}`}></i>

            {!collapsed && (
                <span className={styles.sidebarLabel}>
                    {item.label}
                </span>
            )}
        </Nav.Link>
    )

    return (
        <aside
            className={[
                styles.sidebar,
                collapsed ? styles.sidebarCollapsed : "",
            ].join(" ")}
        >
            <Nav className={`flex-column ${styles.sidebarNav}`}>
                {workspaceItems.length > 0 && (
                    <>
                        {!collapsed && (
                            <div className={styles.sidebarSection}>
                                {t("workspaces", {ns: "navigation"})}
                            </div>
                        )}

                        {workspaceItems.map((item: NavigationItem) => renderItem(item))}
                    </>
                )}

                {administrationItems.length > 0 && (
                    <>
                        <hr className={styles.sidebarDivider}/>

                        {!collapsed && (
                            <div className={styles.sidebarSection}>
                                {t("administration", {ns: "navigation"})}
                            </div>
                        )}

                        {administrationItems.map((item: NavigationItem) => renderItem(item))}
                    </>
                )}
                <hr className={styles.sidebarDivider}/>
                <div className="d-lg-none">
                    {!collapsed && (
                        <div className={styles.sidebarSection}>
                            {t("navigation", {
                                ns: "navigation",
                                defaultValue: "Navigation",
                            })}
                        </div>
                    )}

                    {loggedIn ? (
                        <Nav.Link
                            as={Link}
                            to="/booking"
                            active={active("/booking")}
                            className={styles.sidebarLink}
                            onClick={() => onNavigate?.()}
                        >
                            <i
                                className={`bi bi-ui-checks ${
                                    collapsed ? "" : "me-2"
                                }`}
                            />

                            {!collapsed && (
                                <span className={styles.sidebarLabel}>
                    {t("book.book", {
                        ns: "booking",
                    })}
                </span>
                            )}
                        </Nav.Link>
                    ) : null}

                    <Nav.Link
                        as={Link}
                        to="/"
                        active={location.pathname === "/"}
                        className={styles.sidebarLink}
                        onClick={() => onNavigate?.()}
                    >
                        <i
                            className={`bi bi-exclamation-circle ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("info.title", {
                    ns: "info",
                })}
            </span>
                        )}
                    </Nav.Link>

                    <Nav.Link
                        as={Link}
                        to="/faq"
                        active={active("/faq")}
                        className={styles.sidebarLink}
                        onClick={() => onNavigate?.()}
                    >
                        <i
                            className={`bi bi-question-circle ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("faq.title", {
                    ns: "info",
                })}
            </span>
                        )}
                    </Nav.Link>

                    <Nav.Link
                        as={Link}
                        to="/climate/6784d8aeb2e2dd156850e00a"
                        active={active(
                            "/climate/6784d8aeb2e2dd156850e00a"
                        )}
                        className={styles.sidebarLink}
                        onClick={() => onNavigate?.()}
                    >
                        <i
                            className={`bi bi-thermometer-half ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("site.location", {
                    ns: "site",
                })}
            </span>
                        )}
                    </Nav.Link>

                    <Nav.Link
                        href="https://asset.e-learning.tu-darmstadt.de/d/pruefungondemand_360/"
                        target="_blank"
                        rel="noreferrer"
                        className={styles.sidebarLink}
                        onClick={() => onNavigate?.()}
                    >
                        <i
                            className={`bi bi-compass ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("roomTour", {
                    ns: "navigation",
                })}

                                <i className="bi bi-box-arrow-up-right ms-1"/>
            </span>
                        )}
                    </Nav.Link>

                    <hr className={styles.sidebarDivider}/>

                    {!collapsed && (
                        <div className={styles.sidebarSection}>
                            {t("settings", {
                                ns: "navigation",
                            })}
                        </div>
                    )}

                    <Nav.Link
                        className={styles.sidebarLink}
                        disabled={i18n.language === "de"}
                        onClick={() => {
                            void changeLanguage("de")
                            onNavigate?.()
                        }}
                    >
                        <i
                            className={`bi bi-translate ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("language.german", {
                    ns: "navigation",
                })}
            </span>
                        )}
                    </Nav.Link>

                    <Nav.Link
                        className={styles.sidebarLink}
                        disabled={i18n.language === "en"}
                        onClick={() => {
                            void changeLanguage("en")
                            onNavigate?.()
                        }}
                    >
                        <i
                            className={`bi bi-translate ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("language.english", {
                    ns: "navigation",
                })}
            </span>
                        )}
                    </Nav.Link>

                    <Nav.Link
                        className={styles.sidebarLink}
                        disabled={theme.getTheme() === "dark"}
                        onClick={() => {
                            theme.setTheme("dark")
                            onNavigate?.()
                        }}
                    >
                        <i
                            className={`bi bi-moon-stars-fill ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("theme.dark", {
                    ns: "navigation",
                })}
            </span>
                        )}
                    </Nav.Link>

                    <Nav.Link
                        className={styles.sidebarLink}
                        disabled={theme.getTheme() === "light"}
                        onClick={() => {
                            theme.setTheme("light")
                            onNavigate?.()
                        }}
                    >
                        <i
                            className={`bi bi-brightness-high-fill ${
                                collapsed ? "" : "me-2"
                            }`}
                        />

                        {!collapsed && (
                            <span className={styles.sidebarLabel}>
                {t("theme.light", {
                    ns: "navigation",
                })}
            </span>
                        )}
                    </Nav.Link>

                    {loggedIn ? (
                        <Nav.Link
                            as={Link}
                            to="/settings"
                            active={active("/settings")}
                            className={styles.sidebarLink}
                            onClick={() => onNavigate?.()}
                        >
                            <i
                                className={`bi bi-person-gear ${
                                    collapsed ? "" : "me-2"
                                }`}
                            />

                            {!collapsed && (
                                <span className={styles.sidebarLabel}>
                    {t("user.settings.title", {
                        ns: "settings",
                    })}
                </span>
                            )}
                        </Nav.Link>
                    ) : null}

                    <hr className={styles.sidebarDivider}/>

                    {loggedIn ? (
                        <>
                            <Nav.Link
                                className={styles.sidebarLink}
                                onClick={() => {
                                    auth.logOutLocally()
                                    onNavigate?.()
                                }}
                            >
                                <i
                                    className={`bi bi-box-arrow-right ${
                                        collapsed ? "" : "me-2"
                                    }`}
                                />

                                {!collapsed && (
                                    <span className={styles.sidebarLabel}>
                        {t("logout.local", {
                            ns: "navigation",
                        })}
                    </span>
                                )}
                            </Nav.Link>

                            <Nav.Link
                                className={styles.sidebarLink}
                                onClick={() => {
                                    auth.logOutGlobally()
                                    onNavigate?.()
                                }}
                            >
                                <i
                                    className={`bi bi-box-arrow-right ${
                                        collapsed ? "" : "me-2"
                                    }`}
                                />

                                {!collapsed && (
                                    <span className={styles.sidebarLabel}>
                        {t("logout.global", {
                            ns: "navigation",
                        })}
                    </span>
                                )}
                            </Nav.Link>
                        </>
                    ) : null}
                </div>
            </Nav>
        </aside>
    )
}
